import { GovernorateEnum, DoctorsV2Api } from "../../../shared/api/generated";
import { BaseAuthService } from "./BaseAuthService";
import { AssociationIdData, DoctorInfoData, ClinicInfoData, ClinicPhotoData, DoctorData, SelfieData, IdData } from "../../../shared/types/models/DoctorData";
import { Result } from "../../../shared/api/services/result";
import { SasService } from "../../verification/api/SasService";

import { Governorate } from "../../../shared/types/models/governorate";
import { ApiClient } from "../../../shared/api/services/ApiClient";
import { RegistrationApiService } from "../../registration/api/RegistrationApiService";
import { AuthApiService, ConfirmOTPResponse, QrManualSubmitPayload } from "./AuthApiService";
import { DoctorApiWrapper } from "../../../shared/api/services/DoctorApiWrapper";
import { triggerDoctorInfoRefresh } from '../../../shared/utils/events/DoctorInfoRefreshEvent';

export class DoctorAuthService extends BaseAuthService {
    private doctorsV2Api: DoctorsV2Api;
    private apiWrapper: DoctorApiWrapper;
    private sasService: SasService;
    private registrationApi: RegistrationApiService;
    private authApi: AuthApiService;

    // Request deduplication for getInfo
    private getInfoPromise: Promise<Result<DoctorData>> | null = null;

    constructor() {
        super();
        this.doctorsV2Api = new DoctorsV2Api(new ApiClient());
        this.apiWrapper = new DoctorApiWrapper(this.doctorsV2Api);
        this.sasService = new SasService();
        this.registrationApi = new RegistrationApiService();
        this.authApi = AuthApiService.getInstance();
    }

    async sendOTP(phoneNumber: string) {
        return this.authApi.sendOTP(phoneNumber);
    }

    async confirmOTP(phoneNumber: string, otpCode: string): Promise<Result<ConfirmOTPResponse>> {
        return this.authApi.confirmOTP(phoneNumber, otpCode);
    }

    // License-based OTP flow methods
    async getPhoneByLicense(licenseNumber: string) {
        return this.authApi.getPhoneByLicense(licenseNumber);
    }

    async sendOtpByLicense(licenseNumber: string) {
        return this.authApi.sendOtpByLicense(licenseNumber);
    }

    async confirmOtpByLicense(licenseNumber: string, otpCode: string) {
        return this.authApi.confirmOtpByLicense(licenseNumber, otpCode);
    }

    // IMA ID QR verification fallback (used when DMIS is unavailable)
    async verifyQr(uidOrUrl: string, enteredLicense?: number) {
        return this.authApi.verifyQr(uidOrUrl, enteredLicense);
    }

    async qrManualSubmit(payload: QrManualSubmitPayload) {
        return this.authApi.qrManualSubmit(payload);
    }

    async qrSendOtp(qrPendingToken: string, countryCode: string, phoneNumber: string) {
        return this.authApi.qrSendOtp(qrPendingToken, countryCode, phoneNumber);
    }

    async qrVerifyOtp(qrPendingToken: string, countryCode: string, phoneNumber: string, otpCode: string) {
        return this.authApi.qrVerifyOtp(qrPendingToken, countryCode, phoneNumber, otpCode);
    }

    async logout() {
        return this.authApi.logout();
    }

    async submitSelfie(data: SelfieData): Promise<Result<DoctorData>> {
        try {
            const selfieResult = data.selfie ? Result.success(data.selfie) : await this.sasService.uploadImage(data.selfieFile!);
            if (!selfieResult.isSuccess) {
                return this.handleError(selfieResult.error!);
            }
            const res = await this.apiWrapper.doctorSignupFacePhoto(selfieResult.data!);
            if (res.statusCode === 200) {
                // Inform the app that profile has updated
                triggerDoctorInfoRefresh();
                return Result.success({});
            } else if (res.statusCode === 404) {
                // Even 404 might mean progress updated – still refresh
                triggerDoctorInfoRefresh();
                return Result.success({});
            }
            return this.handleError(res.errorMessage);
        } catch (error) {
            return this.handleError(error);
        }
    }

    async submitId(data: IdData): Promise<Result<DoctorData>> {
        try {
            const idFrontResult = data.idFront ? Result.success(data.idFront) : await this.sasService.uploadImage(data.idFrontFile!);
            if (!idFrontResult.isSuccess) {
                return this.handleError(idFrontResult.error!);
            }
            const idBackResult = data.idBack ? Result.success(data.idBack) : await this.sasService.uploadImage(data.idBackFile!);
            if (!idBackResult.isSuccess) {
                return this.handleError(idBackResult.error!);
            }
            const res = await this.apiWrapper.doctorSignupNationalIdPhoto(
                idFrontResult.data!,
                idBackResult.data!
            );
            if (res.statusCode === 200) {
                // Inform the app that profile has updated
                triggerDoctorInfoRefresh();
                return Result.success({});
            } else if (res.statusCode === 404) {
                // Even 404 might mean progress updated – still refresh
                triggerDoctorInfoRefresh();
                return Result.success({});
            }
            return this.handleError(res.errorMessage);
        } catch (error) {
            return this.handleError(error);
        }
    }

    async getInfo(): Promise<Result<DoctorData>> {
        // If there's already a request in flight, return it
        if (this.getInfoPromise) {
            return this.getInfoPromise;
        }

        // Create the request promise
        this.getInfoPromise = (async () => {
            try {
                // Use the auth API to get doctor info first
                const doctorResult = await this.authApi.getMyInfo();
                if (!doctorResult.isSuccess) {
                    return this.handleError(doctorResult.error);
                }
                
                const apiPayload = doctorResult.data as any;
                const doctorInfo = apiPayload.doctor_info || apiPayload;
                
                return Result.success({
                    status: doctorInfo.status,
                    registration_progress: doctorInfo.registration_progress || undefined,
                });
            } catch (error) {
                return this.handleError(error);
            }
        })();

        // Clean up the promise after completion
        this.getInfoPromise.finally(() => {
            this.getInfoPromise = null;
        });

        return this.getInfoPromise;
        
        /* Original code for reference - will be used when doctor API is ready
        const res = await this.doctorsV2Api.myInfoV2();
        if (res.status) {
            const doctorInfo = res.data!.doctorInfo;
            const doctorInfo = res.data!.doctorInfo;
            const clinicInfo = res.data!.clinicInfo;
            const doctorDocs = res.data!.doctorDocs;
            const clinicDocs = res.data!.clinicDocs;
            const openPermit = clinicDocs?.find(doc => doc.docType === ClinicDocType.License)?.privateFileId;
            var openPermitFile: File | null = null;
            if (openPermit && openPermit.url) {
                openPermitFile = (await this.sasService.getImage(openPermit.url!)).data!;
            }
            const associationIdFront = doctorDocs?.find(doc => doc.docType === DoctorDocType.MedicalLicenseBack)?.privateFileId;
            var associationIdFrontFile: File | null = null;
            if (associationIdFront && associationIdFront.url) {
                associationIdFrontFile = (await this.sasService.getImage(associationIdFront.url!)).data!;
            }
            const associationIdBack = doctorDocs?.find(doc => doc.docType === DoctorDocType.MedicalLicenseBack)?.privateFileId;
            var associationIdBackFile: File | null = null;
            if (associationIdBack && associationIdBack.url) {
                associationIdBackFile = (await this.sasService.getImage(associationIdBack.url!)).data!;
            }
            const clinicOutside = clinicDocs?.find(doc => doc.docType === ClinicDocType.FrontPhoto)?.privateFileId;
            var clinicOutsideFile: File | null = null;
            if (clinicOutside && clinicOutside.url) {
                clinicOutsideFile = (await this.sasService.getImage(clinicOutside.url!)).data!;
            }
            const clinicInside = clinicDocs?.find(doc => doc.docType === ClinicDocType.InsidePhoto)?.privateFileId;
            var clinicInsideFile: File | null = null;
            if (clinicInside && clinicInside.url) {
                clinicInsideFile = (await this.sasService.getImage(clinicInside.url!)).data!;
            }
            const selfie = doctorDocs?.find(doc => doc.docType === DoctorDocType.FacePhoto)?.privateFileId;
            var selfieFile: File | null = null;
            if (selfie && selfie.url) {
                selfieFile = (await this.sasService.getImage(selfie.url!)).data!;
            }
            return Result.success({
                email: doctorInfo?.email,
                phone: doctorInfo?.phoneNumber,

                fullName: doctorInfo?.fullName,
                sex: (() => {
                    switch (doctorInfo?.sex) {
                        case SexEnum.Male:
                            return Sex.MALE;
                        case SexEnum.Female:
                            return Sex.FEMALE;
                        default:
                            return undefined;
                    }
                })(),
                speciality: doctorInfo?.specialty ? specialties.find((specialty) => specialty.title === doctorInfo.specialty) : undefined,
                subSpeciality: doctorInfo?.subSpecialty as SubSpecialty | undefined,

                openPermit: openPermit,
                openPermitFile: openPermitFile,
                governorate: clinicInfo?.governorate as Governorate | undefined,
                address: clinicInfo?.address,
                clinicId: clinicInfo?.clinicLicenseNumber,
                taxNumber: clinicInfo?.taxNumber,
                phoneNumber: clinicInfo?.phoneNumber1,
                secondaryPhoneNumber: clinicInfo?.phoneNumber2,

                selfie: selfie,
                selfieFile: selfieFile,

                associationIdFront: associationIdFront,
                associationIdFrontFile: associationIdFrontFile,
                associationIdBack: associationIdBack,
                associationIdBackFile: associationIdBackFile,
                clinicOutside: clinicOutside,
                clinicOutsideFile: clinicOutsideFile,
                clinicInside: clinicInside,
                clinicInsideFile: clinicInsideFile,
                status: res.data!.doctorInfo?.status,
            });
        }
        return this.handleError(res.errorMessage);
        */
    }

    async submitAssociationId(data: AssociationIdData): Promise<Result<boolean>> {
        const frontSideResult = data.associationIdBack ? Result.success(data.associationIdBack) : await this.sasService.uploadImage(data.associationIdFrontFile!);
        if (!frontSideResult.isSuccess) {
            return this.handleError(frontSideResult.error!);
        }
        const backSideResult = data.associationIdBack ? Result.success(data.associationIdBack) : await this.sasService.uploadImage(data.associationIdBackFile!);
        if (!backSideResult.isSuccess) {
            return this.handleError(backSideResult.error!);
        }
        try {
            const res = await this.apiWrapper.doctorSignupStep2V2(
                frontSideResult.data!,
                backSideResult.data!
            )
            if (res.status) {
                // Inform the app that profile has updated
                triggerDoctorInfoRefresh();
                return Result.success(true);
            }
            return this.handleError(res.errorMessage);
        } catch (error) {
            return this.handleError(error);
        }
    }

    async submitDoctorInfo(data: DoctorInfoData): Promise<Result<boolean>> {
        try {
            console.log('🔐 AuthService.submitDoctorInfo called with:', data);
            // Pass the data to the registration API
            const result = await this.registrationApi.submitDoctorInfo(data as any);
            
            console.log('📊 RegistrationApi result:', result);
            if (result.isSuccess) {
                console.log('🔄 Refreshing doctor info after successful submission...');
                // Refresh doctor info after successful submission
                triggerDoctorInfoRefresh();
                console.log('✅ Doctor info refreshed, returning success');
                return Result.success(true);
            }
            
            console.log('❌ RegistrationApi call failed:', result.error);
            return Result.failure(result.error || 'Failed to submit doctor info');
        } catch (error) {
            console.error('💥 Error in submitDoctorInfo:', error);
            return this.handleError(error);
        }
    }

    // Legacy code removed to fix compilation errors

    async submitClinicInfo(data: ClinicInfoData): Promise<Result<boolean>> {
        try {
            const openPermitResult = data.openPermit ? Result.success(data.openPermit) : await this.sasService.uploadImage(data.openPermitFile!);
            if (!openPermitResult.isSuccess) {
                return this.handleError(openPermitResult.error!);
            }
            const res = await this.apiWrapper.signupStep4V2({
                governorate: data.governorate ? Governorate[data.governorate as keyof typeof Governorate] as GovernorateEnum : undefined,
                address: data.address,
                clinicLicenseNumber: data.clinicId,
                taxNumber: data.taxNumber,
                clinicLicensePhoto: openPermitResult.data!,
                phoneNumber1: data.phoneNumber,
                phoneNumber2: data.secondaryPhoneNumber,
            })
            if (res.status) {
                // Inform the app that profile has updated
                triggerDoctorInfoRefresh();
                return Result.success(true);
            }
            return this.handleError(res.errorMessage);
        } catch (error) {
            return this.handleError(error);
        }
    }

    async submitClinicPhoto(data: ClinicPhotoData): Promise<Result<boolean>> {
        try {
            const clinicOutsideResult = data.clinicOutside ? Result.success(data.clinicOutside) : await this.sasService.uploadImage(data.clinicOutsideFile!);
            if (!clinicOutsideResult.isSuccess) {
                return this.handleError(clinicOutsideResult.error!);
            }
            const clinicInsideResult = data.clinicInside ? Result.success(data.clinicInside) : await this.sasService.uploadImage(data.clinicInsideFile!);
            if (!clinicInsideResult.isSuccess) {
                return this.handleError(clinicInsideResult.error!);
            }
            const res = await this.apiWrapper.signupStep5V2({
                clinicOutsidePhoto: clinicOutsideResult.data!,
                clinicInsidePhoto: clinicInsideResult.data!,
            })
            if (res.status) {
                // Inform the app that profile has updated
                triggerDoctorInfoRefresh();
                return Result.success(true);
            }
            return this.handleError(res.errorMessage);
        } catch (error) {
            return this.handleError(error);
        }
    }

    async skipClinicPhoto(): Promise<Result<boolean>> {
        try {
            const res = await this.apiWrapper.signupStep5V2()
            if (res.status) {
                // Inform the app that profile has updated
                triggerDoctorInfoRefresh();
                return Result.success(true);
            }
            return this.handleError(res.errorMessage);

        } catch (error) {
            return this.handleError(error);
        }
    }
}